import { ROLE_LABELS } from '@company/shared'
import { createFileRoute } from '@tanstack/react-router'
import {
  Calendar,
  Hash,
  Loader2,
  Mail,
  Monitor,
  Phone,
  Slack,
  User,
  Users,
  Building2,
} from 'lucide-react'
import { useState } from 'react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/components/ui/toast'
import { useMyProfile, useUpdateMyProfile, useUpdateMyAvatar } from '@/hooks/hr'
import { useUploadFile } from '@/hooks/system'

export const Route = createFileRoute('/_authenticated/settings/profile')({
  component: ProfilePage,
})

const STATUS_LABELS: Record<string, string> = {
  active: '在职',
  on_leave: '休假中',
  resigned: '已离职',
}

const TEAM_ROLE_LABELS: Record<string, string> = {
  product: '产品',
  design: '设计',
  frontend: '前端',
  backend: '后端',
  testing: '测试',
  finance: '财务',
  hr: '人事',
  admin: '行政',
}

const DEVICE_TYPE_LABELS: Record<string, string> = {
  computer: '电脑',
  monitor: '显示器',
  keyboard: '键盘',
  mouse: '鼠标',
  other: '其他',
}

function ProfilePage() {
  const { data: profile, isLoading, error } = useMyProfile()
  const updateProfile = useUpdateMyProfile()
  const updateAvatar = useUpdateMyAvatar()
  const uploadFile = useUploadFile()
  const toast = useToast()

  const [isEditingPhone, setIsEditingPhone] = useState(false)
  const [phoneValue, setPhoneValue] = useState('')

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    )
  }

  if (error || !profile) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-destructive">加载个人资料失败</p>
      </div>
    )
  }

  const handlePhoneEdit = () => {
    setPhoneValue(profile.phone || '')
    setIsEditingPhone(true)
  }

  const handlePhoneSave = async () => {
    try {
      await updateProfile.mutateAsync({ phone: phoneValue || null })
      setIsEditingPhone(false)
      toast.success('手机号更新成功')
    } catch {
      toast.error('更新失败')
    }
  }

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) {
      return
    }

    // 验证文件类型
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('请上传 JPEG、PNG 或 WebP 格式的图片')
      return
    }

    // 验证文件大小
    if (file.size > 5 * 1024 * 1024) {
      toast.error('图片大小不能超过 5MB')
      return
    }

    try {
      const result = await uploadFile.mutateAsync({ file, options: { category: 'avatar' } })
      await updateAvatar.mutateAsync(result.storagePath)
      toast.success('头像更新成功')
    } catch {
      toast.error('上传失败')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-blue-500">
          <User className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">个人资料</h1>
          <p className="text-muted-foreground">查看和管理您的个人信息</p>
        </div>
      </div>

      {/* Profile Header Card */}
      <div className="bg-card rounded-lg border p-6">
        <div className="flex items-start gap-6">
          <div className="relative">
            <Avatar className="h-24 w-24">
              <AvatarImage src={profile.avatar || undefined} alt={profile.name} />
              <AvatarFallback className="text-2xl">{profile.name.slice(0, 2)}</AvatarFallback>
            </Avatar>
            <label className="bg-primary text-primary-foreground hover:bg-primary/90 absolute bottom-0 right-0 cursor-pointer rounded-full p-1.5">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => void handleAvatarChange(e)}
              />
              <User className="h-3 w-3" />
            </label>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold">{profile.name}</h2>
              <Badge variant={profile.status === 'active' ? 'default' : 'secondary'}>
                {STATUS_LABELS[profile.status] || profile.status}
              </Badge>
            </div>
            <p className="text-muted-foreground">{ROLE_LABELS[profile.role] || profile.role}</p>
            {profile.position && (
              <p className="text-muted-foreground text-sm">{profile.position.name}</p>
            )}
          </div>
        </div>
      </div>

      {/* Basic Info */}
      <div className="bg-card rounded-lg border">
        <div className="border-b p-4">
          <h3 className="font-semibold">基本信息</h3>
        </div>
        <div className="grid gap-4 p-4 sm:grid-cols-2">
          <InfoItem icon={Hash} label="员工编号" value={profile.employeeNo} />
          <InfoItem icon={Mail} label="邮箱" value={profile.email} />
          <div className="flex items-center gap-3">
            <Phone className="text-muted-foreground h-4 w-4" />
            <div className="flex-1">
              <p className="text-muted-foreground text-sm">手机号</p>
              {isEditingPhone ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={phoneValue}
                    onChange={(e) => setPhoneValue(e.target.value)}
                    placeholder="请输入手机号"
                    className="h-8"
                  />
                  <Button
                    size="sm"
                    onClick={() => void handlePhoneSave()}
                    disabled={updateProfile.isPending}
                  >
                    保存
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setIsEditingPhone(false)}>
                    取消
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="font-medium">{profile.phone || '-'}</p>
                  <Button size="sm" variant="ghost" onClick={handlePhoneEdit}>
                    编辑
                  </Button>
                </div>
              )}
            </div>
          </div>
          <InfoItem icon={Calendar} label="入职日期" value={profile.hireDate} />
          {profile.department && (
            <InfoItem icon={Building2} label="部门" value={profile.department.name} />
          )}
          <div className="flex items-center gap-3">
            <Slack className="text-muted-foreground h-4 w-4" />
            <div>
              <p className="text-muted-foreground text-sm">Slack 绑定</p>
              <p className="font-medium">
                {profile.slackUserId ? (
                  <span className="text-green-600">已绑定</span>
                ) : (
                  <span className="text-muted-foreground">未绑定</span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Team & Projects */}
      <div className="bg-card rounded-lg border">
        <div className="border-b p-4">
          <h3 className="flex items-center gap-2 font-semibold">
            <Users className="h-4 w-4" />
            团队与项目
          </h3>
        </div>
        <div className="p-4">
          {profile.team ? (
            <div className="space-y-4">
              <div>
                <p className="text-muted-foreground text-sm">所属团队</p>
                <div className="flex items-center gap-2">
                  <p className="font-medium">{profile.team.name}</p>
                  <Badge variant="outline">
                    {TEAM_ROLE_LABELS[profile.team.role] || profile.team.role}
                  </Badge>
                  {profile.team.isLeader && <Badge>负责人</Badge>}
                </div>
              </div>
              {profile.projects.length > 0 && (
                <div>
                  <p className="text-muted-foreground mb-2 text-sm">参与项目</p>
                  <div className="flex flex-wrap gap-2">
                    {profile.projects.map((project) => (
                      <Badge key={project.id} variant="secondary">
                        {project.name} ({project.code})
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground">暂未加入任何团队</p>
          )}
        </div>
      </div>

      {/* Devices */}
      <div className="bg-card rounded-lg border">
        <div className="border-b p-4">
          <h3 className="flex items-center gap-2 font-semibold">
            <Monitor className="h-4 w-4" />
            分配设备
          </h3>
        </div>
        <div className="p-4">
          {profile.devices.length > 0 ? (
            <div className="space-y-3">
              {profile.devices.map((device) => (
                <div
                  key={device.id}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div>
                    <p className="font-medium">{device.name}</p>
                    <p className="text-muted-foreground text-sm">
                      {DEVICE_TYPE_LABELS[device.type] || device.type} · {device.serialNumber}
                    </p>
                  </div>
                  <p className="text-muted-foreground text-sm">{device.assignedAt.split('T')[0]}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">暂无分配设备</p>
          )}
        </div>
      </div>
    </div>
  )
}

interface InfoItemProps {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string
}

function InfoItem({ icon: Icon, label, value }: InfoItemProps) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="text-muted-foreground h-4 w-4" />
      <div>
        <p className="text-muted-foreground text-sm">{label}</p>
        <p className="font-medium">{value}</p>
      </div>
    </div>
  )
}
